import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

let pending = false;
const sourceId = 'cloud-gift-wrap-toggle';
const checkoutStates = new Map();

const rows = () => document.querySelectorAll('cloud-gift-wrap-toggle');

function setBusy(value) {
  pending = value;
  for (const row of rows()) {
    if (value) row.setAttribute('aria-busy', 'true');
    else row.removeAttribute('aria-busy');
    const button = row.querySelector('[role="switch"]');
    if (value) {
      button.dataset.wasDisabled = String(button.disabled);
      button.disabled = true;
    } else if (button.dataset.wasDisabled !== undefined) {
      button.disabled = button.dataset.wasDisabled === 'true';
      delete button.dataset.wasDisabled;
    }
  }
  if (value) {
    for (const button of document.querySelectorAll('[name="checkout"]')) {
      if (!checkoutStates.has(button)) checkoutStates.set(button, button.disabled);
      button.disabled = true;
    }
  } else {
    for (const [button, wasDisabled] of checkoutStates) button.disabled = wasDisabled;
    checkoutStates.clear();
  }
}

function message(text, error = false) {
  for (const row of rows()) {
    const status = row.querySelector('.cloud-gift-wrap__message');
    status.toggleAttribute('data-error', error);
    status.textContent = text;
  }
}

async function request(path, body) {
  const response = await fetch(window.Shopify.routes.root + path, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.errors) throw new Error('Gift wrap request failed');
  return data;
}

function sectionIds() {
  return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map(element => element.dataset.sectionId))].slice(0, 5);
}

async function refresh(cart, sections, ids) {
  for (const id of ids) {
    if (!sections[id]) sections[id] = await sectionRenderer.getSectionHTML(id, false);
  }
  document.dispatchEvent(new CartUpdateEvent(cart, sourceId, {
    itemCount: cart.item_count,
    source: sourceId,
    sections,
  }));
}

class CloudGiftWrapToggle extends HTMLElement {
  connectedCallback() {
    if (pending) {
      const button = this.querySelector('[role="switch"]');
      if (button && button.dataset.wasDisabled === undefined) {
        button.dataset.wasDisabled = String(button.disabled);
        button.disabled = true;
      }
    }
  }
}

if (!customElements.get('cloud-gift-wrap-toggle')) {
  customElements.define('cloud-gift-wrap-toggle', CloudGiftWrapToggle);

  document.addEventListener('click', async event => {
    const button = event.target instanceof Element ? event.target.closest('cloud-gift-wrap-toggle [role="switch"]') : null;
    if (!button || button.disabled || pending) return;
    event.preventDefault();

    const row = button.closest('cloud-gift-wrap-toggle');
    const variantId = Number(row.dataset.variantId);
    const desired = button.getAttribute('aria-checked') !== 'true';
    const errorText = row.dataset.error;
    const successText = desired ? row.dataset.added : row.dataset.removed;
    const ids = sectionIds();
    const shouldRestoreFocus = document.activeElement === button;
    message('');
    setBusy(true);

    try {
      let cart = await request('cart.js');
      const existing = cart.items.filter(item => Number(item.variant_id) === variantId);
      let sections = {};
      const render = { sections: ids, sections_url: window.location.pathname };

      if (desired && existing.length === 0) {
        const result = await request('cart/add.js', {
          items: [{ id: variantId, quantity: 1 }],
          ...render,
        });
        sections = result.sections || {};
        cart = await request('cart.js');
      } else if (!desired && existing.length > 0) {
        cart = await request('cart/update.js', {
          updates: Object.fromEntries(existing.map(item => [item.key, 0])),
          ...render,
        });
        sections = cart.sections || {};
      }

      await refresh(cart, sections, ids);
      message(successText);
    } catch {
      try {
        const cart = await request('cart.js');
        await refresh(cart, {}, ids);
      } catch {}
      message(errorText, true);
    } finally {
      setBusy(false);
      if (shouldRestoreFocus) {
        document.querySelector('cloud-gift-wrap-toggle [role="switch"]')?.focus({ preventScroll: true });
      }
    }
  });

  document.addEventListener('click', event => {
    if (!pending || !(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin === window.location.origin && /\/checkout(?:\/|$)/.test(url.pathname)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('submit', event => {
    if (pending && event.submitter?.name === 'checkout') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
}
